import PersonOutlined from '@mui/icons-material/PersonOutlined';
import PictureInPictureOutlined from '@mui/icons-material/PictureInPictureOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import type { IconType } from 'react-icons';
import { FaEquals } from 'react-icons/fa6';
import { GoFileMedia } from 'react-icons/go';
import type { TranslationKey } from '@/i18n/translation-key';
import {
  DEFAULT_PLAYLIST_NEXT_SHOW_MODE,
  PLAYLIST_NEXT_SHOW_MODES,
  type ExplicitPlaylistNextShowMode,
  type PlaylistNextShowMode,
} from '@/types/dtos/media/playlist-next-show-mode.enum';

export function resolvePlaylistNextShowMode(value?: PlaylistNextShowMode | string | null): PlaylistNextShowMode {
  if (value === 'mediaOnly' || value === 'both' || value === 'speakerOnly' || value === 'inherit') {
    return value;
  }
  return DEFAULT_PLAYLIST_NEXT_SHOW_MODE;
}

export function cyclePlaylistNextShowMode(current?: PlaylistNextShowMode | string | null): PlaylistNextShowMode {
  const resolved = resolvePlaylistNextShowMode(current);
  const index = PLAYLIST_NEXT_SHOW_MODES.indexOf(resolved);
  const nextIndex = index < 0 ? 0 : (index + 1) % PLAYLIST_NEXT_SHOW_MODES.length;
  return PLAYLIST_NEXT_SHOW_MODES[nextIndex]!;
}

export function toExplicitPlaylistNextShowMode(
  value?: PlaylistNextShowMode | string | null,
): ExplicitPlaylistNextShowMode | undefined {
  const resolved = resolvePlaylistNextShowMode(value);
  return resolved === 'inherit' ? undefined : resolved;
}

/**
 * Resolve the Next-preview show mode for a cue row preselect.
 * `inherit` ("same as predecessor") copies the currently on-air / played-out mode.
 */
export function resolveNextShowModeFromPreselect(
  preselect: PlaylistNextShowMode | string | null | undefined,
  predecessorShowMode: ExplicitPlaylistNextShowMode,
): ExplicitPlaylistNextShowMode {
  return toExplicitPlaylistNextShowMode(preselect) ?? predecessorShowMode;
}

/**
 * Dedup key for applying Next preselect. Includes performMode so sim→live re-syncs
 * stagedSelection even when the resolved mode string is unchanged.
 */
export function buildNextShowModeApplyKey(args: {
  performMode: string;
  cueIdentity: string;
  preselect: PlaylistNextShowMode | string | null | undefined;
  resolved: ExplicitPlaylistNextShowMode;
  predecessorShowMode: ExplicitPlaylistNextShowMode;
}): string {
  const isInherit = resolvePlaylistNextShowMode(args.preselect) === 'inherit';
  if (isInherit) {
    return `${args.performMode}|${args.cueIdentity}|inherit|${args.predecessorShowMode}`;
  }
  return `${args.performMode}|${args.cueIdentity}|${args.resolved}`;
}

/**
 * Operator NEXT overlay (or equivalent) holds preselect re-apply for the current Next identity.
 * Stale holds for a previous identity are cleared so the new Next can apply.
 */
export function shouldHoldNextShowModePreselect(
  cueIdentity: string,
  heldCueIdentity: string | null | undefined,
): { hold: boolean; clearStaleHold: boolean } {
  if (!heldCueIdentity) {
    return { hold: false, clearStaleHold: false };
  }
  if (heldCueIdentity === cueIdentity) {
    return { hold: true, clearStaleHold: false };
  }
  return { hold: false, clearStaleHold: true };
}

type NextShowModeDisplay = {
  Icon: SvgIconComponent | IconType;
  tooltipKey: TranslationKey;
};

export const PLAYLIST_NEXT_SHOW_MODE_DISPLAY: Record<PlaylistNextShowMode, NextShowModeDisplay> = {
  inherit: { Icon: FaEquals, tooltipKey: 'media.playlistNextShowMode.inherit' },
  mediaOnly: { Icon: GoFileMedia, tooltipKey: 'media.playlistNextShowMode.mediaOnly' },
  both: { Icon: PictureInPictureOutlined, tooltipKey: 'media.playlistNextShowMode.both' },
  speakerOnly: { Icon: PersonOutlined, tooltipKey: 'media.playlistNextShowMode.speakerOnly' },
};

export function getPlaylistNextShowModeIconColor(mode: PlaylistNextShowMode): 'action' | 'info' {
  return mode === 'inherit' ? 'action' : 'info';
}

export function getPlaylistNextShowModeDisplay(value?: PlaylistNextShowMode | string | null): NextShowModeDisplay {
  return PLAYLIST_NEXT_SHOW_MODE_DISPLAY[resolvePlaylistNextShowMode(value)];
}

export { PLAYLIST_NEXT_SHOW_MODES };
